import type { SxProps, Theme } from '@mui/material';
import { stepConnectorClasses } from '@mui/material';

// ─── Root page layout ─────────────────────────────────────────────────────────

export const registrationRootSx: SxProps<Theme> = {
  display: 'flex',
  width: '100%',
  minHeight: '100vh'
};

// ─── Custom step icon ─────────────────────────────────────────────────────────

export const getCustomStepIconSx = (
  active: boolean,
  completed: boolean
): SxProps<Theme> => ({
  width: '32px',
  height: '32px',
  borderRadius: '9999px',
  background: completed ? '#20842F' : active ? '#000000' : '#D6D7D9',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
});

export const getCustomStepIconNumberSx = (active: boolean): SxProps<Theme> => ({
  fontSize: '13px',
  fontWeight: 700,
  lineHeight: 1,
  color: active ? '#fff' : '#000000'
});

// ─── Left decorative panel ────────────────────────────────────────────────────

export const leftDecorativePanelSx: SxProps<Theme> = {
  display: { xs: 'none', md: 'block' },
  width: { md: 'calc(100vw - 70%)', lg: 'max(404px, 30vw)' },
  flexShrink: 0,
  alignSelf: 'stretch',
  position: 'relative',
  background: 'linear-gradient(180deg, #A7D0E3 0%, #FFFFFF 100%)',
  overflow: 'hidden',
  height: '100vh',
  transition:
    'height 0.6s cubic-bezier(0.4, 0, 0.2, 1), width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  willChange: 'height, width'
};

export const airplaneImageSx: SxProps<Theme> = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  transition:
    'height 0.6s cubic-bezier(0.4, 0, 0.2, 1), width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  willChange: 'height, width'
};

// ─── Main content panel (right side) ─────────────────────────────────────────

export const mainContentPanelSx: SxProps<Theme> = {
  flex: { md: '0 0 70%', lg: 1 },
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  mt: { xs: 2, sm: 0 },
  gap: { xs: '24px', sm: '48px' },
  pt: { xs: 0, sm: '48px' },
  pr: { xs: '16px', lg: '120px', sm: 5 },
  pb: '48px',
  pl: { xs: '16px', sm: '48px' },
  background: {
    xs: '#F9F9F9',
    sm: 'linear-gradient(180deg, #A8D1E4 0%, #F9F9F9 24.5%)',
    md: '#F9F9F9'
  },
  boxSizing: 'border-box',
  opacity: 1
};

// ─── Page title area ──────────────────────────────────────────────────────────

export const pageTitleWrapperSx: SxProps<Theme> = {
  mx: { xs: '-16px', sm: 0 },
  px: { xs: '16px', sm: 0 },
  pt: { xs: '40px', sm: 0 },
  pb: { xs: '40px', sm: 0 },
  background: {
    xs: 'linear-gradient(180deg, #A8D1E4 0%, #F9F9F9 100%)',
    sm: 'transparent'
  },
  display: 'flex',
  flexDirection: 'column',
  gap: '24px'
};

export const pageTitleSx: SxProps<Theme> = {
  fontFamily: "'AC Nord Text', 'AC Nord', sans-serif",
  fontWeight: 700,
  fontSize: { xs: '24px', sm: '28px' },
  lineHeight: { xs: '32px', sm: '36px' },
  letterSpacing: '0px',
  color: '#111',
  my: { xs: 2, sm: 0 },
  textAlign: { xs: 'left' }
};

export const pageBodySX: SxProps<Theme> = {
  fontFamily: "'AC Nord Text', 'AC Nord', sans-serif",
  fontWeight: 400,
  fontSize: { xs: '18px', sm: '16px' },
  lineHeight: { xs: '24px', sm: '22px' },
  letterSpacing: '0px',
  color: '#000000'
};

// ─── Visually-hidden / screen-reader-only utility ─────────────────────────────

export const srOnlySx: SxProps<Theme> = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0
};

// ─── Stepper ──────────────────────────────────────────────────────────────────

export const stepConnectorSx: SxProps<Theme> = {
  display: { xs: 'none', sm: 'block' },
  [`&.${stepConnectorClasses.vertical}`]: {
    marginLeft: '15px',
    padding: 0
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderLeftWidth: '2px',
    borderLeftStyle: 'solid',
    borderColor: '#D6D7D9',
    minHeight: '26px'
  }
};

export const stepperSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: '868px',
  padding: 0
};

export const stepSx: SxProps<Theme> = {
  mb: { xs: '24px', sm: 0 }
};

export const stepLabelSx: SxProps<Theme> = {
  padding: 0,
  alignItems: 'center',
  '& .MuiStepLabel-iconContainer': {
    paddingRight: { xs: '16px', sm: '32px' }
  },
  '& .MuiStepLabel-label': {
    fontFamily: "'AC Nord Text', 'AC Nord', sans-serif",
    fontWeight: 500,
    fontSize: { xs: '18px', sm: '20px' },
    lineHeight: { xs: '26px', sm: '28px' },
    letterSpacing: '0px',
    color: '#A3A4A6',
    margin: 0,
    '&.Mui-active': {
      fontWeight: 700,
      color: '#000000'
    },
    '&.Mui-completed': {
      fontWeight: 500,
      color: '#A3A4A6'
    }
  }
};

export const stepContentSx: SxProps<Theme> = {
  marginTop: 0,
  marginLeft: { xs: 0, sm: '15px' },
  paddingLeft: { xs: 0, sm: '49px' },
  paddingRight: 0,
  paddingTop: 0,
  paddingBottom: 0,
  borderLeft: { xs: 'none', sm: '2px solid #D6D7D9' },
  '&.MuiStepContent-last': {
    borderLeft: 'none'
  }
};

export const stepContentInnerBoxSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  pt: '8px'
};

// ─── Step 1 — description text ────────────────────────────────────────────────

export const step1DescriptionSx: SxProps<Theme> = {
  fontFamily: "'AC Nord Text', 'AC Nord', sans-serif",
  fontWeight: 400,
  fontSize: { xs: '14px', sm: '16px' },
  lineHeight: '22px',
  letterSpacing: '0px',
  color: '#000000',
  m: 0,
  pl: { sm: 0, xs: '48px' }
};
